import type { Color } from "../../shared/colors";
import {
  buildMeshCircles,
  buildMeshPalette,
  buildMeshSquares,
  buildTickerColors,
  CircleMeshSvg,
  type MeshWeight,
  randomMeshColor,
  seededMeshRandom,
  shuffleMeshColors,
} from "./CircleMeshSvg";

export const shuffleColors = shuffleMeshColors;

type Props = {
  seed?: string;
  targetCircleSize?: number;
  mutingIntensity?: number;
  weights?: MeshWeight[];
  animate?: boolean;
  animateFade?: boolean;
  animateAll?: boolean;
  restrictedColors?: Color[];
  grayscale?: boolean;
};

export function CircleMesh({
  seed,
  targetCircleSize = 100,
  mutingIntensity = 0.5,
  weights: weightsProp,
  animate = true,
  animateFade = true,
  animateAll = false,
  restrictedColors,
  grayscale = false,
}: Props) {
  const weights =
    weightsProp ?? (mutingIntensity === 0 ? [400, 600] : [200, 400]);
  return (
    <CircleMeshSvg
      seed={seed}
      targetCircleSize={targetCircleSize}
      mutingOpacity={0.4}
      animate={animate}
      animateFade={animateFade}
      grayscale={grayscale}
      build={({ rows, columns, seed: buildSeed }) => {
        const random = seededMeshRandom(buildSeed);
        const count = rows * columns;
        const vividPalette = buildMeshPalette(
          random,
          count,
          weights,
          restrictedColors
        );
        const mutedPalette =
          mutingIntensity > 0
            ? buildMeshPalette(random, count, [200, 400], restrictedColors)
            : vividPalette;
        const getColor = (muted: boolean) =>
          randomMeshColor(random, muted ? mutedPalette : vividPalette);

        const { circles, grid } = buildMeshCircles(
          rows,
          columns,
          (row, col, previousRows) => {
            let muted = mutingIntensity >= 1;
            if (!muted && mutingIntensity > 0) {
              muted = !!(
                previousRows[row - 1]?.[col]?.muted ||
                previousRows[row - 1]?.[col - 1]?.muted ||
                previousRows[row - 1]?.[col + 1]?.muted ||
                random() < mutingIntensity ** 2
              );
            }
            const getTickerColor = () => getColor(muted);
            const color = getTickerColor();
            const tickerColors = buildTickerColors(getTickerColor);
            const hasTicker = animateAll || random() < 0.5;
            return {
              color,
              tickerColors: hasTicker ? tickerColors : [],
              muted,
            };
          }
        );

        const squareVisuals = new Map<
          string,
          { color: string; muted: boolean }
        >();
        const squares = buildMeshSquares(rows, columns, (row, col) => {
          const key = `${row}-${col}`;
          const existing = squareVisuals.get(key);
          if (existing) return existing;
          const bottomLeft = grid[row - 1]?.[col - 1];
          const bottomRight = grid[row - 1]?.[col];
          const topLeft = grid[row]?.[col - 1];
          const topRight = grid[row]?.[col];
          const muted =
            mutingIntensity > 0 &&
            !!(
              bottomLeft?.muted ||
              bottomRight?.muted ||
              (topLeft?.muted && topRight?.muted) ||
              (topLeft?.muted && !topRight) ||
              (topRight?.muted && !topLeft)
            );
          const visual = { color: getColor(muted), muted };
          squareVisuals.set(key, visual);
          return visual;
        });

        return { circles, squares };
      }}
    />
  );
}
